// Client-side palette extraction from an image. No server involved.
// Uses a downscaled canvas + coarse quantization for speed on any device.

export async function extractPaletteFromImage(file, count = 6) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file"));
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const maxSide = 200;
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        URL.revokeObjectURL(url);

        // Coarse bucket: quantize to 5 bits per channel (32^3 buckets).
        const buckets = new Map();
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 200) continue; // skip semi-transparent
          const r = data[i] & 0xF8;
          const g = data[i + 1] & 0xF8;
          const b = data[i + 2] & 0xF8;
          // Skip near-white/near-black extremes so we get "story" colors, not paper/ink.
          const bright = (r + g + b) / 3;
          if (bright < 20 || bright > 245) continue;
          const key = (r << 16) | (g << 8) | b;
          const entry = buckets.get(key);
          if (entry) { entry.n += 1; entry.r += data[i]; entry.g += data[i + 1]; entry.b += data[i + 2]; }
          else buckets.set(key, { n: 1, r: data[i], g: data[i + 1], b: data[i + 2] });
        }
        // Sort by frequency
        const sorted = [...buckets.values()].sort((a, b) => b.n - a.n);
        // Dedupe similar colors (perceptual distance threshold)
        const chosen = [];
        for (const c of sorted) {
          const r = Math.round(c.r / c.n);
          const g = Math.round(c.g / c.n);
          const b = Math.round(c.b / c.n);
          const hex = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
          if (chosen.every((h) => colorDistance(h, hex) > 40)) chosen.push(hex);
          if (chosen.length >= count) break;
        }
        resolve(chosen);
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not load image")); };
    img.src = url;
  });
}

function colorDistance(hexA, hexB) {
  const [ar, ag, ab] = hexToRgb(hexA);
  const [br, bg, bb] = hexToRgb(hexB);
  return Math.sqrt((ar - br) ** 2 + (ag - bg) ** 2 + (ab - bb) ** 2);
}
function hexToRgb(hex) {
  const v = hex.replace("#", "");
  const n = parseInt(v, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
